import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "../components/Seo";
import { isBusinessEmail, isValidEmailFormat } from "../lib/businessEmail";
import { LEADS_ENDPOINT } from "../lib/siteConfig";

const DOMAIN_PATTERN = /^(?!https?:\/\/)(?!.*\s)([a-z0-9-]+\.)+[a-z]{2,}$/i;

const FIELDS = [
  { name: "domain", label: "Company Domain", placeholder: "example.com", icon: "language", type: "text" },
  { name: "company", label: "Company Name", placeholder: "Acme Corp", icon: "business", type: "text" },
  { name: "email", label: "Official Work Email", placeholder: "you@company.com", icon: "mail", type: "email" },
  { name: "phone", label: "Phone Number (Optional)", placeholder: "+1 (555) 000-0000", icon: "call", type: "tel" },
];

function validate(form) {
  const errors = {};
  if (!form.domain.trim()) {
    errors.domain = "Enter your company domain.";
  } else if (!DOMAIN_PATTERN.test(form.domain.trim())) {
    errors.domain = "Enter a valid domain, e.g. yourcompany.com — no https:// needed.";
  }

  if (!form.company.trim()) {
    errors.company = "Enter your company name.";
  }

  if (!form.email.trim()) {
    errors.email = "Enter your official work email.";
  } else if (!isValidEmailFormat(form.email)) {
    errors.email = "Enter a valid email address.";
  } else if (!isBusinessEmail(form.email)) {
    errors.email = "Please use your official company email — not Gmail, Yahoo, Hotmail, or ProtonMail.";
  }

  return errors;
}

export default function FreeScan() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    domain: searchParams.get("domain") || "",
    company: "",
    email: "",
    phone: "",
    honey: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.honey) return;

    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setStatus("submitting");
    try {
      const res = await fetch(LEADS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: `Free Leak Report request — ${form.domain}`,
          _template: "table",
          _captcha: "false",
          Company: form.company,
          Domain: form.domain,
          Email: form.email,
          Phone: form.phone,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 50%, rgba(249,115,22,0.05) 0%, transparent 50%), radial-gradient(circle at 85% 30%, rgba(0,81,213,0.05) 0%, transparent 50%)",
        }}
      />

      <main className="flex-grow flex flex-col items-center justify-center px-gutter w-full max-w-container-max mx-auto relative z-10 py-16 md:py-section-gap">
        <Seo
          title="Free Domain Leak Report"
          description="Enter your company domain and get a free, detailed report on corporate credential exposure found in stealer malware logs — delivered straight to your official company email."
          path="/free-scan"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-section-gap items-center w-full max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-stack-lg max-w-xl"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary-container px-3 py-1.5 rounded-full w-fit">
              <span className="material-symbols-outlined text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                timer
              </span>
              <span className="font-eyebrow text-eyebrow tracking-widest uppercase">Limited-Time Offer</span>
            </div>
            <h1 className="font-display-hero-mobile text-display-hero-mobile md:font-display-hero md:text-display-hero text-on-surface dark:text-surface-bright">
              Get a Free Leak &amp; Exposure Report for Your Domain
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant dark:text-surface-variant">
              Tell us your company domain and we'll scan stealer malware logs for corporate
              credential exposure tied to your organization — then send a detailed report
              straight to your official inbox.
            </p>
            <div className="flex flex-col gap-4 mt-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-secondary" style={{ fontVariationSettings: "'FILL' 1" }}>
                    shield_lock
                  </span>
                </div>
                <div>
                  <h3 className="font-headline-card text-headline-card text-on-surface dark:text-surface-bright">
                    Secure &amp; Confidential
                  </h3>
                  <p className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant mt-1">
                    Reports are delivered directly to verified corporate email addresses only.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-primary-container" style={{ fontVariationSettings: "'FILL' 1" }}>
                    radar
                  </span>
                </div>
                <div>
                  <h3 className="font-headline-card text-headline-card text-on-surface dark:text-surface-bright">
                    Deep Web Scanning
                  </h3>
                  <p className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant mt-1">
                    We analyze millions of records across stealer logs and dark web marketplaces.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="w-full max-w-md mx-auto relative group"
          >
            <div className="absolute -top-[1px] left-0 right-0 h-[2px] bg-gradient-to-r from-primary-container to-transparent z-20 rounded-t-lg opacity-80" />
            <div className="bg-surface-card-light dark:bg-surface-card-dark backdrop-blur-[12px] border border-glass rounded-lg p-card-padding shadow-[0_20px_40px_rgba(0,0,0,0.05)] relative z-10 transition-transform duration-300 group-hover:-translate-y-1">
              {status === "success" ? (
                <div className="text-center py-8">
                  <span className="material-symbols-outlined text-emerald-500 text-5xl mb-4 block">check_circle</span>
                  <h2 className="font-headline-card text-headline-card text-on-surface dark:text-surface-bright mb-2">
                    Request received
                  </h2>
                  <p className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant">
                    Thanks, {form.company}! Our team is scanning{" "}
                    <span className="font-semibold text-on-surface dark:text-surface-bright">{form.domain}</span> now.
                    You'll receive a detailed leak report at{" "}
                    <span className="font-semibold text-on-surface dark:text-surface-bright">{form.email}</span>{" "}
                    within 1–2 business days.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-stack-lg">
                  <input
                    type="text"
                    value={form.honey}
                    onChange={(e) => handleChange("honey", e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                    aria-hidden="true"
                  />

                  {FIELDS.map((field) => (
                    <div key={field.name} className="flex flex-col gap-2 relative">
                      <label className="font-label-sm text-label-sm text-on-surface dark:text-surface-bright" htmlFor={field.name}>
                        {field.label}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="material-symbols-outlined text-tertiary-container text-[20px]">{field.icon}</span>
                        </div>
                        <input
                          id={field.name}
                          type={field.type}
                          placeholder={field.placeholder}
                          value={form[field.name]}
                          onChange={(e) => handleChange(field.name, e.target.value)}
                          className={`w-full bg-white/50 dark:bg-white/5 border rounded pl-10 pr-4 py-3 font-body-md text-body-md text-on-surface dark:text-surface-bright focus:ring-1 focus:ring-primary-container focus:outline-none transition-colors ${
                            errors[field.name] ? "border-severity-critical" : "border-glass focus:border-primary-container"
                          }`}
                        />
                      </div>
                      {errors[field.name] && (
                        <p className="text-xs text-severity-critical">{errors[field.name]}</p>
                      )}
                    </div>
                  ))}

                  {status === "error" && (
                    <div className="flex items-start gap-2 rounded-DEFAULT border border-severity-critical/30 bg-severity-critical/10 px-4 py-3 text-sm text-severity-critical">
                      <span className="material-symbols-outlined text-[18px] shrink-0">error</span>
                      <span>Something went wrong sending your request. Please try again.</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="mt-2 bg-primary-container text-white font-eyebrow text-[14px] tracking-wider uppercase py-4 px-6 rounded hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed transition-transform duration-200 shadow-md flex justify-center items-center gap-2"
                  >
                    {status === "submitting" ? "Sending..." : "Request Free Report"}
                    <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
                  </button>
                  <p className="text-[11px] text-tertiary-container text-center mt-2 font-body-md">
                    We only use this information to prepare and deliver your report. No spam,
                    ever.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </main>
    </>
  );
}
